import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Snackbar,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  styled,
  Grid,
  Divider,
  Stack,
  FormHelperText,
  Collapse,
  IconButton,
} from '@mui/material';
import { ProviderBillingInfo } from '../../types/types';
import APIService from '../../services/APIService';
import { useAccessToken, useUserState, useSubjectLanguageVariants } from '@features/user';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PaymentIcon from '@mui/icons-material/Payment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { MuiTelInput } from 'mui-tel-input';

// Extend ProviderBillingInfo to include email_address and fax_number
interface ExtendedProviderBillingInfo extends ProviderBillingInfo {
  email_address?: string;
  fax_number?: string;
}

// Define UserInfo interface to match the structure in userState
interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  mobile_phone_num?: string;
  name?: string;
  [key: string]: any; // Allow for other properties
}

interface ProviderInformationViewProps {
  highlightedFields?: string[];
  onProviderSave?: (updatedProvider: ExtendedProviderBillingInfo) => void;
  fieldKeys?: string[]; // Add field filtering capability
  paperProps?: React.ComponentProps<typeof Paper>; // For customizing the Paper component
  initialProviderInfo?: Partial<ExtendedProviderBillingInfo>; // Add initialProviderInfo prop
}

const HighlightedTextField = styled(TextField)<{ ishighlighted: string }>(
  ({ theme, ishighlighted }) => ({
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: ishighlighted === 'true' ? theme.palette.primary.main : 'inherit',
        borderWidth: 2,
      },
    },
  }),
);

const ProviderInformationView: React.FC<ProviderInformationViewProps> = ({
  highlightedFields = [],
  onProviderSave,
  fieldKeys,
  paperProps = {},
  initialProviderInfo = {},
}) => {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  // State for collapsible sections
  const [sectionsExpanded, setSectionsExpanded] = useState({
    personal: true,
    practice: true,
    facility: false,
    billing: false,
  });

  const [providerInfo, setProviderInfo] = useState<ExtendedProviderBillingInfo>({
    first_name: '',
    last_name: '',
    npi_number: '',
    billing_npi_number: '',
    facility_address: '',
    facility_address2: '',
    city: '',
    state: '',
    tax_id: '',
    zip: '',
    phone: '',
    email_address: '', // Add email field
    fax_number: '', // Add fax field
    tax_id_type: '',
    accept_insurance_payment_as_full: '',
    place_of_service: '',
    communications_name: '',
    signing_name: '',
    ...initialProviderInfo, // Apply initial values passed from props
  });

  const { getAccessToken } = useAccessToken();
  const userState = useUserState();
  const subjectLanguageVariants = useSubjectLanguageVariants();

  const isEhrMode = userState?.featureFlags?.ehr_mode ?? false;
  const subjectLanguage = subjectLanguageVariants.singular; // For backward compatibility

  // Refs for tracking changes
  const originalProviderInfoRef = useRef<ExtendedProviderBillingInfo>(providerInfo);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    fetchProviderInfo();
  }, [getAccessToken]);

  // Update when initialProviderInfo changes
  useEffect(() => {
    if (initialProviderInfo && Object.keys(initialProviderInfo).length > 0) {
      setProviderInfo(prev => ({
        ...prev,
        ...initialProviderInfo,
      }));
    }
  }, [initialProviderInfo]);

  const fetchProviderInfo = async () => {
    setLoading(true);
    try {
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIGetRequest({
        requestString: `/user/getProviderBillingInfo`,
        accessToken,
      });

      console.log('Provider Info Response:', response);
      if (response.ok) {
        const data = response.value.provider_billing_info || {};

        // Get user info for auto-population
        const userInfo: UserInfo = userState?.userInfo || {};

        // Merge data from provider_billing_info and user_info
        // Prefer provider_billing_info values if they exist
        const mergedData = {
          // Auto-populate from user_info if not in provider_billing_info
          first_name: data.first_name || userInfo.firstName || '',
          last_name: data.last_name || userInfo.lastName || '',
          email_address: data.email_address || userInfo.email || '',
          phone: data.phone || userInfo.mobile_phone_num || '',
          communications_name:
            data.communications_name ||
            `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() ||
            '',
          ...(isEhrMode
            ? {
                signing_name:
                  data.signing_name ||
                  `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() ||
                  '',
              }
            : {}),

          // The rest of provider data
          ...data,

          // Apply initialProviderInfo values with highest priority
          ...initialProviderInfo,
        };

        setProviderInfo(prev => ({
          ...prev,
          ...mergedData,
        }));

        // Update original ref to prevent immediate save
        originalProviderInfoRef.current = {
          ...originalProviderInfoRef.current,
          ...mergedData,
        };
      } else {
        throw new Error('Failed to fetch provider information');
      }
    } catch (error) {
      console.error('Error fetching provider information:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load provider data',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>,
  ) => {
    const { name, value } = event.target as HTMLInputElement | { name: string; value: string };
    const newProviderInfo = {
      ...providerInfo,
      [name]: value,
    };
    setProviderInfo(newProviderInfo);
    triggerAutosave(newProviderInfo);
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    const newProviderInfo = {
      ...providerInfo,
      [name]: value as string,
    };
    setProviderInfo(newProviderInfo);
    triggerAutosave(newProviderInfo);
  };

  const toggleSection = (section: 'personal' | 'practice' | 'facility' | 'billing') => {
    setSectionsExpanded(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Autosave function
  const performSave = useCallback(
    async (dataToSave: ExtendedProviderBillingInfo) => {
      // Only save if there are actual changes
      const hasChanges =
        JSON.stringify(dataToSave) !== JSON.stringify(originalProviderInfoRef.current);
      if (!hasChanges) return;

      setSaveStatus('saving');
      try {
        const accessToken = await getAccessToken();
        const response = await APIService.makeAPIPostRequest({
          requestString: '/user/updateProviderBillingInfo',
          accessToken,
          body: {
            updated_fields: dataToSave,
          },
        });

        if (response.ok) {
          setSaveStatus('saved');
          originalProviderInfoRef.current = dataToSave;
          if (onProviderSave) onProviderSave(dataToSave);
        } else {
          throw new Error('Failed to update provider information');
        }
      } catch (error) {
        console.error('Error updating provider information:', error);
        setSaveStatus('error');
        setSnackbar({
          open: true,
          message: 'Failed to save changes. Please try again.',
          severity: 'error',
        });

        // Hide error status after 3 seconds
        setTimeout(() => setSaveStatus('idle'), 3000);
      }
    },
    [getAccessToken, onProviderSave],
  );

  // Trigger autosave with debounce
  const triggerAutosave = useCallback(
    (newProviderInfo: ExtendedProviderBillingInfo) => {
      // Clear any existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Set new timeout for 3 seconds
      saveTimeoutRef.current = setTimeout(() => {
        performSave(newProviderInfo);
      }, 3000);
    },
    [performSave],
  );

  // Handle blur to save immediately
  const handleBlur = useCallback(() => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    // Save immediately
    performSave(providerInfo);
  }, [providerInfo, performSave]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  // Define all available fields
  const allFields = [
    { key: 'first_name', label: 'First Name', type: 'text' },
    { key: 'last_name', label: 'Last Name', type: 'text' },
    {
      key: 'communications_name',
      label: 'Communications Name',
      type: 'text',
      tooltip: `This name will be shown in communications sent to ${subjectLanguage}s`,
    },
    ...(isEhrMode
      ? [
          {
            key: 'signing_name',
            label: 'Signature Name',
            type: 'text',
            tooltip: 'Your signature will appear at the end of notes you sign.',
          },
        ]
      : []),
    { key: 'email_address', label: 'Email Address', type: 'email' },
    { key: 'phone', label: 'Phone', type: 'phone' },
    { key: 'npi_number', label: 'Rendering NPI Number', type: 'number' },
    {
      key: 'billing_npi_number',
      label: 'Billing NPI Number',
      type: 'number',
    },
    { key: 'fax_number', label: 'Fax Number', type: 'phone' },
    ...(isEhrMode
      ? [
          {
            key: 'license_number',
            label: 'License Number',
            type: 'text',
          },
          {
            key: 'license_state',
            label: 'License State',
            type: 'text',
          },
        ]
      : []),
    { key: 'facility_address', label: 'Facility Address', type: 'text' },
    {
      key: 'facility_address2',
      label: 'Facility Address 2',
      type: 'text',
    },
    { key: 'city', label: 'City', type: 'text' },
    { key: 'state', label: 'State', type: 'text' },
    { key: 'zip', label: 'Zip', type: 'text' },
    { key: 'tax_id', label: 'Tax ID', type: 'text' },
    { key: 'tax_id_type', label: 'Tax ID Type', type: 'select' },
    {
      key: 'accept_insurance_payment_as_full',
      label: 'Accept Insurance Payment as Full',
      type: 'select',
    },
    { key: 'place_of_service', label: 'Place of Service', type: 'select' },
  ];

  // Filter fields if fieldKeys is provided
  const fieldsToShow = fieldKeys
    ? allFields.filter(field => fieldKeys.includes(field.key))
    : allFields;

  // Group fields by section
  const personalFields = fieldsToShow.filter(f =>
    [
      'first_name',
      'last_name',
      'communications_name',
      'signing_name',
      'email_address',
      'phone',
    ].includes(f.key),
  );
  const practiceFields = fieldsToShow.filter(f =>
    ['npi_number', 'billing_npi_number', 'fax_number', 'license_number', 'license_state'].includes(
      f.key,
    ),
  );
  const facilityFields = fieldsToShow.filter(f =>
    ['facility_address', 'facility_address2', 'city', 'state', 'zip'].includes(f.key),
  );
  const billingFields = fieldsToShow.filter(f =>
    ['tax_id', 'tax_id_type', 'accept_insurance_payment_as_full', 'place_of_service'].includes(
      f.key,
    ),
  );

  const renderField = (field: any) => {
    // Handle different field types
    if (field.type === 'phone') {
      return (
        <Box key={field.key}>
          <MuiTelInput
            label={field.label}
            value={providerInfo[field.key as keyof ExtendedProviderBillingInfo] || ''}
            onChange={newValue => {
              const newProviderInfo = {
                ...providerInfo,
                [field.key]: newValue,
              };
              setProviderInfo(newProviderInfo);
              triggerAutosave(newProviderInfo);
            }}
            defaultCountry="US"
            forceCallingCode
            onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
            size="small"
            fullWidth
            onBlur={handleBlur}
            sx={{
              '& input': {
                height: '1.4375em',
              },
              ...(highlightedFields.includes(field.key) && {
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 2,
                  },
                },
              }),
            }}
          />
          {field.tooltip && <FormHelperText sx={{ mt: 0.5 }}>{field.tooltip}</FormHelperText>}
        </Box>
      );
    } else if (field.type === 'select') {
      if (field.key === 'tax_id_type') {
        return (
          <Box key={field.key}>
            <FormControl
              fullWidth
              sx={{
                ...(highlightedFields.includes(field.key) && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }),
              }}
            >
              <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
              <Select
                labelId={`${field.key}-label`}
                label={field.label}
                name={field.key}
                value={providerInfo[field.key as keyof ExtendedProviderBillingInfo] || ''}
                onChange={handleSelectChange}
                variant="outlined"
                size="small"
                onBlur={handleBlur}
              >
                <MenuItem value="EIN">EIN (Employer Identification Number)</MenuItem>
                <MenuItem value="SSN">SSN (Social Security Number)</MenuItem>
              </Select>
            </FormControl>
            <FormHelperText sx={{ mt: 0.5 }}>
              EIN is for businesses, SSN is for individuals
            </FormHelperText>
          </Box>
        );
      } else if (field.key === 'accept_insurance_payment_as_full') {
        return (
          <Box key={field.key}>
            <FormControl
              fullWidth
              sx={{
                ...(highlightedFields.includes(field.key) && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }),
              }}
            >
              <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
              <Select
                labelId={`${field.key}-label`}
                label={field.label}
                name={field.key}
                value={providerInfo[field.key as keyof ExtendedProviderBillingInfo] || ''}
                onChange={handleSelectChange}
                variant="outlined"
                size="small"
                onBlur={handleBlur}
              >
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
            <FormHelperText sx={{ mt: 0.5 }}>
              Whether you accept the insurance company's approved amount as full payment
            </FormHelperText>
          </Box>
        );
      } else if (field.key === 'place_of_service') {
        return (
          <Box key={field.key}>
            <FormControl
              fullWidth
              sx={{
                ...(highlightedFields.includes(field.key) && {
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: 2,
                    },
                  },
                }),
              }}
            >
              <InputLabel id={`${field.key}-label`}>{field.label}</InputLabel>
              <Select
                labelId={`${field.key}-label`}
                label={field.label}
                name={field.key}
                value={providerInfo[field.key as keyof ExtendedProviderBillingInfo] || ''}
                onChange={handleSelectChange}
                variant="outlined"
                size="small"
                onBlur={handleBlur}
              >
                <MenuItem value="Office">Office (11)</MenuItem>
                <MenuItem
                  value={`Telehealth Provided Other than in ${subjectLanguageVariants.singularCap}'s Home`}
                >
                  Telehealth - Other Location (02)
                </MenuItem>
                <MenuItem
                  value={`Telehealth Provided in ${subjectLanguageVariants.singularCap}'s Home`}
                >
                  Telehealth - {subjectLanguageVariants.singularCap}'s Home (10)
                </MenuItem>
                <MenuItem value="Home">Home Visit (12)</MenuItem>
                <MenuItem value="Outpatient Hospital">Outpatient Hospital (22)</MenuItem>
                <MenuItem value="Community Mental Health Center (CMHC)">
                  Community Mental Health Center (53)
                </MenuItem>
                <MenuItem value="Skilled Nursing Facility">Skilled Nursing Facility (31)</MenuItem>
              </Select>
            </FormControl>
            <FormHelperText sx={{ mt: 0.5 }}>
              Most providers use "Office (11)" for in-person visits
            </FormHelperText>
          </Box>
        );
      }
    } else if (field.type === 'select') {
      // Handle other select types
      return null;
    }

    // Default text field for other types
    return (
      <Box key={field.key}>
        <HighlightedTextField
          label={field.label}
          name={field.key}
          type={field.type || 'text'}
          value={providerInfo[field.key as keyof ExtendedProviderBillingInfo] || ''}
          onChange={handleInputChange}
          variant="outlined"
          size="small"
          fullWidth
          onBlur={handleBlur}
          ishighlighted={highlightedFields.includes(field.key).toString()}
        />
        {field.tooltip && <FormHelperText sx={{ mt: 0.5 }}>{field.tooltip}</FormHelperText>}
      </Box>
    );
  };

  return (
    <Box sx={{ ...paperProps.sx }} {...paperProps}>
      {/* Save Status Indicator */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, height: '24px' }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.875rem',
            opacity: saveStatus !== 'idle' ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
          }}
        >
          {saveStatus === 'saving' && 'Saving...'}
          {saveStatus === 'saved' && 'Changes saved'}
          {saveStatus === 'error' && 'Error saving changes'}
        </Typography>
      </Box>

      {/* Important Information Alert */}
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mb: 4 }}>
        <Typography variant="body2">
          No need to fill all this in right now! Most fields are optional and can be filled in as
          needed while using JotPsych.
        </Typography>
      </Alert>

      <Stack spacing={4}>
        {/* Personal Information Section */}
        {personalFields.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 3,
              transition: 'box-shadow 0.3s ease-in-out',
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                cursor: 'pointer',
              }}
              onClick={() => toggleSection('personal')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Personal Information
                </Typography>
              </Box>
              <IconButton size="small">
                {sectionsExpanded.personal ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: sectionsExpanded.personal ? 2 : 0 }}
            >
              Your name, contact details, and how you'd like to be addressed in {subjectLanguage}{' '}
              communications
            </Typography>
            <Collapse in={sectionsExpanded.personal}>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {personalFields.map(field => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Paper>
        )}

        {/* Practice Information Section */}
        {practiceFields.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 3,
              transition: 'box-shadow 0.3s ease-in-out',
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                cursor: 'pointer',
              }}
              onClick={() => toggleSection('practice')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BusinessIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Practice Information
                </Typography>
              </Box>
              <IconButton size="small">
                {sectionsExpanded.practice ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: sectionsExpanded.practice ? 2 : 0 }}
            >
              Professional identifiers, NPI numbers, and fax number
            </Typography>
            <Collapse in={sectionsExpanded.practice}>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {practiceFields.map(field => (
                  <Grid item xs={12} sm={6} key={field.key}>
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Paper>
        )}

        {/* Facility Information Section */}
        {facilityFields.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 3,
              transition: 'box-shadow 0.3s ease-in-out',
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                cursor: 'pointer',
              }}
              onClick={() => toggleSection('facility')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Facility Information
                </Typography>
              </Box>
              <IconButton size="small">
                {sectionsExpanded.facility ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: sectionsExpanded.facility ? 2 : 0 }}
            >
              Physical practice location details used for billing and claims
            </Typography>
            <Collapse in={sectionsExpanded.facility}>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {facilityFields.map(field => (
                  <Grid
                    item
                    xs={12}
                    sm={
                      field.key === 'facility_address' || field.key === 'facility_address2' ? 12 : 6
                    }
                    key={field.key}
                  >
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Paper>
        )}

        {/* Billing Information Section */}
        {billingFields.length > 0 && (
          <Paper
            elevation={2}
            sx={{
              p: 3,
              transition: 'box-shadow 0.3s ease-in-out',
              '&:hover': {
                boxShadow: 4,
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
                cursor: 'pointer',
              }}
              onClick={() => toggleSection('billing')}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PaymentIcon sx={{ mr: 1.5, color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  Billing Information
                </Typography>
              </Box>
              <IconButton size="small">
                {sectionsExpanded.billing ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: sectionsExpanded.billing ? 2 : 0 }}
            >
              Tax identification, insurance payment preferences, and service location codes for
              billing and claims
            </Typography>
            <Collapse in={sectionsExpanded.billing}>
              <Divider sx={{ mb: 3 }} />
              <Grid container spacing={2}>
                {billingFields.map(field => (
                  <Grid
                    item
                    xs={12}
                    sm={
                      field.key === 'place_of_service' ||
                      field.key === 'accept_insurance_payment_as_full'
                        ? 12
                        : 6
                    }
                    key={field.key}
                  >
                    {renderField(field)}
                  </Grid>
                ))}
              </Grid>
            </Collapse>
          </Paper>
        )}
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProviderInformationView;
